import _ from 'lodash';

import { AppSettings } from '@/app/AppSettings';
import { Log } from '@/parsing/Log';
import { LogEntry, LogEntryType } from '@/parsing/LogEntry';

import { LogViewerPostProcessing } from '../../LogViewerPostProcessing';
import {
  FoundOccurance,
  FoundOccurancesPerHighlighter,
  PostProcessingResult,
} from '../../post-processing/PostProcessor';
import {
  buildIosRouterTreeSnapshotsByLogEntry,
  IosRouterTreeSnapshot,
} from '../../timeline/generators/ios/IosRouterTree';
import {
  LogViewerTableColumnDefinition,
  LogViewerTableColumnType,
  LogViewerTableNetworkBodyWithJqColumnDefinition,
} from '../LogViewerTableColumn';
import {
  LogViewerTableRowModel,
  LogViewerTableRowModelDataWithCells,
  LogViewerTableRowModelDelegate,
  LogViewerTableRowType,
} from '../LogViewerTableRowModel';
import { FileDividerRowModelFactory } from '../rows/file-divider/FileDividerRowModel';
import { LogEntryGenericCellModelFactory } from '../rows/log-entry/cells/LogEntryGenericCell';
import { LogEntryMQTTCellModelFactory } from '../rows/log-entry/cells/LogEntryMQTTCell';
import { LogEntryNetworkCellModelFactory } from '../rows/log-entry/cells/LogEntryNetworkCellModel';
import { LogEntryReduxActionCellModelFactory } from '../rows/log-entry/cells/LogEntryReduxActionCell';
import {
  NetworkBodyWithJqCellModel,
  NetworkBodyWithJqCellModelFactory,
} from '../rows/log-entry/cells/NetworkBodyWithJqCell';
import { LogEntryCellModel, LogEntryCellModelData } from '../rows/log-entry/LogEntryCellModel';
import {
  LogEntryRowModel,
  LogEntryRowModelData,
  LogEntryRowModelDelegate,
  LogEntryRowModelFactory,
} from '../rows/log-entry/LogEntryRowModel';
import { LogViewerTableSummaryRowModelFactory } from '../rows/summary/SummaryRowModel';
import { SyncLogEntryRowChangesArgs } from './ClientToServerOperations';
import {
  LogViewerTableModelWorkerClientDelegate,
  makeLogViewerTableModelWorkerClient,
} from './LogViewerTableModelWorkerClient';
import { modelDataDiffMerge } from './modelDataDiffMerge';

export interface ModelChangeContext {
  shouldClearRowHeightCache: boolean;
  rowIndicesBeforeFilteringToRefresh?: number[];
}

export interface LogViewerTableModelData {
  appSettings: AppSettings;
  baseTimeOffsetRowIndex: number;
  postProcessing: LogViewerPostProcessing;

  selectedRowIndexBeforeFiltering?: number;
  bookmarkedRowIndicesBeforeFiltering: Set<number>;
}

export interface LogViewerTableModelDataWithRows {
  tableData: LogViewerTableModelData;
  allRows: LogViewerTableRowModelDataWithCells[];
}

export interface LogViewerTableModelDelegate {
  onModelChange(ctx: ModelChangeContext): void;
  onBookmarkedRowsChange(models: LogViewerTableRowModel[]): void;
  onRequestScrollToRowWithIndexAfterFiltering(rowIndex: number): void;
  onRequestJumpToLogEntry(logEntryIndex: number): void;
  onNoBugButtonFound?(): void;

  postProcessingOperationStarted(): void;
  postProcessingOperationIsAboutToComplete(): void;
  postProcessingOperationCompleted(occurancesPerHighlighter: FoundOccurancesPerHighlighter[]): void;
  postProcessingOperationFailed(error: any): void;
}

export interface LogViewerTableModel
  extends LogViewerTableRowModelDelegate,
    LogEntryRowModelDelegate {
  get modelData(): LogViewerTableModelData;

  convertLogEntryIndexToRowIndexBeforeFiltering(logEntryIndex: number): number | undefined;
  getFoundOccurance(highlighterId: string, termIndex: number): FoundOccurance | undefined;
  deleteBookmarkWithIndexBeforeFiltering(indexBeforeFiltering: number): void;
  deleteAllBookmarks(): void;

  componentWillUnmount(): void;
  reset(args: { log: Log; settings: AppSettings; postProcessing: LogViewerPostProcessing }): void;
  set baseTimeOffsetRowIndex(rowIndex: number);
  set settings(s: AppSettings);
  set postProcessing(pp: LogViewerPostProcessing);

  get selectedRowIndexBeforeFiltering(): number | undefined;
  set selectedRowIndexBeforeFiltering(v: number | undefined);

  get allRowModels(): LogViewerTableRowModel[];
  getRowsCountToDisplay(): number;
  getRowModelToDisplayAt(index: number): LogViewerTableRowModel;

  get additionalColumnDefinitions(): LogViewerTableColumnDefinition[];
  createColumn(def: LogViewerTableColumnDefinition): void;
  updateCellsForColumn(index: number): void;
  deleteColumnAtIndex(index: number): void;
  deleteAllColumns(): void;
}

export function makeLogViewerTableModel(args: {
  log: Log;
  postProcessing: LogViewerPostProcessing;
  settings: AppSettings;
  delegate: LogViewerTableModelDelegate;
}): LogViewerTableModel {
  return new LogViewerTableModelImpl(args);
}

class LogViewerTableModelImpl
  implements
    LogViewerTableRowModelDelegate,
    LogEntryRowModelDelegate,
    LogViewerTableModelWorkerClientDelegate
{
  private delegate: LogViewerTableModelDelegate;
  private _log: Log;

  private workerClient = makeLogViewerTableModelWorkerClient();

  private _allRowModels: LogViewerTableRowModel[] = [];
  private logEntryIndexToRowIndexBeforeFiltering: Map<number, number> = new Map();

  private _additionalColumnDefinitions: LogViewerTableColumnDefinition[] = [];
  get additionalColumnDefinitions(): LogViewerTableColumnDefinition[] {
    return this._additionalColumnDefinitions;
  }

  private foundOccurancesByHighlighter: FoundOccurancesPerHighlighter[] = [];
  private rowIndicesToShow: number[] = [];
  private _modelData: LogViewerTableModelData;
  private iosRouterTreeSnapshotsByLogEntry: Map<number, IosRouterTreeSnapshot> = new Map();

  get allRowModels(): LogViewerTableRowModel[] {
    return this._allRowModels;
  }

  get modelData(): LogViewerTableModelData {
    return this._modelData;
  }

  convertLogEntryIndexToRowIndexBeforeFiltering(logEntryIndex: number): number | undefined {
    return this.logEntryIndexToRowIndexBeforeFiltering.get(logEntryIndex);
  }

  constructor(args: {
    log: Log;
    postProcessing: LogViewerPostProcessing;
    settings: AppSettings;
    delegate: LogViewerTableModelDelegate;
  }) {
    this._log = args.log;
    this.delegate = args.delegate;

    this._modelData = {
      appSettings: _.cloneDeep(args.settings),
      baseTimeOffsetRowIndex: 0,
      postProcessing: _.cloneDeep(args.postProcessing),
      bookmarkedRowIndicesBeforeFiltering: new Set(),
    };

    this.workerClient.delegate = this;

    this.constructInitialState();

    if (args.settings.autoBugButtonTimeReference) {
      const found = this.applyAutoBugButtonTimeReference();
      if (!found) {
        this.delegate.onNoBugButtonFound?.();
      }
    }
  }

  private notifyAboutChange(ctx: ModelChangeContext) {
    this.delegate.onModelChange(ctx);
  }

  componentWillUnmount(): void {
    this.workerClient.shutdown();
  }

  getFoundOccurance(highlighterId: string, termIndex: number): FoundOccurance | undefined {
    for (const o of this.foundOccurancesByHighlighter) {
      if (o.highlighterId === highlighterId) {
        return o.array[termIndex];
      }
    }

    return undefined;
  }

  deleteBookmarkWithIndexBeforeFiltering(indexBeforeFiltering: number) {
    this._allRowModels[indexBeforeFiltering].data.isBookmarked = false;
    this.modelData.bookmarkedRowIndicesBeforeFiltering.delete(indexBeforeFiltering);

    this.notifyAboutChange({
      shouldClearRowHeightCache: false,
      rowIndicesBeforeFilteringToRefresh: [indexBeforeFiltering],
    });
    this.notifyAboutChangedBookmarkedRows();
  }

  deleteAllBookmarks() {
    const indices = Array.from(this._modelData.bookmarkedRowIndicesBeforeFiltering);
    indices.forEach((index) => {
      this._allRowModels[index].data.isBookmarked = false;
    });

    this._modelData.bookmarkedRowIndicesBeforeFiltering.clear();

    this.notifyAboutChange({
      shouldClearRowHeightCache: false,
      rowIndicesBeforeFilteringToRefresh: indices,
    });
    this.notifyAboutChangedBookmarkedRows();
  }

  reset(args: { log: Log; settings: AppSettings; postProcessing: LogViewerPostProcessing }): void {
    if (this._log.uniqueId === args.log.uniqueId) {
      this.settings = args.settings;
      this.postProcessing = args.postProcessing;
    } else {
      this._log = args.log;
      this.saveSettingsAndApplyToRows(args.settings);
      this._modelData.postProcessing = _.cloneDeep(args.postProcessing);

      this.constructInitialState();
      this.notifyAboutChange({ shouldClearRowHeightCache: true });
      this.notifyAboutChangedBookmarkedRows();

      if (args.settings.autoBugButtonTimeReference) {
        const found = this.applyAutoBugButtonTimeReference();
        if (!found) {
          this.delegate.onNoBugButtonFound?.();
        }
      }
    }
  }

  set baseTimeOffsetRowIndex(rowIndex: number) {
    if (this._modelData.baseTimeOffsetRowIndex !== rowIndex) {
      const baseRowModel = this._allRowModels[rowIndex] as LogEntryRowModel;
      const baseLogEntry = baseRowModel.logEntry;
      const baseTimeOffsetInSeconds = baseLogEntry.secondsSinceFirstLogEntry;

      this._modelData.baseTimeOffsetRowIndex = rowIndex;

      const changesToSync: SyncLogEntryRowChangesArgs = { changes: [], postProcess: true };

      this._allRowModels.forEach((model) => {
        switch (model.data.type) {
          case LogViewerTableRowType.Summary:
            break;
          case LogViewerTableRowType.LogEntry:
            const logEntryRow = model as LogEntryRowModel;
            logEntryRow.data.header.isBaseTimeOffsetFromThisRow =
              model.data.indexBeforeApplyingFilters === rowIndex;
            logEntryRow.setBaseTimeOffset(
              logEntryRow.logEntry.secondsSinceFirstLogEntry - baseTimeOffsetInSeconds,
            );

            changesToSync.changes.push({
              rowIndex: model.data.indexBeforeApplyingFilters,
              change: {
                header: {
                  isBaseTimeOffsetFromThisRow: logEntryRow.data.header.isBaseTimeOffsetFromThisRow,
                  baseTimeOffset: logEntryRow.data.header.baseTimeOffset,
                },
              },
            });
            break;
        }
      });

      this.notifyAboutChange({ shouldClearRowHeightCache: false });
      this.workerClient.syncLogEntryRowChanges(changesToSync);
    }
  }

  set settings(s: AppSettings) {
    if (!_.isEqual(s, this._modelData.appSettings)) {
      const prevAutoBug = this._modelData.appSettings.autoBugButtonTimeReference;
      this.saveSettingsAndApplyToRows(s);
      this.notifyAboutChange({ shouldClearRowHeightCache: false });

      this.workerClient.syncTableChanges({
        postProcess: false,
        change: {
          appSettings: this._modelData.appSettings,
        },
      });

      // Only apply (and potentially toast) when the setting is freshly toggled ON,
      // not on every unrelated settings change (e.g. case-sensitive toggle).
      if (s.autoBugButtonTimeReference && !prevAutoBug) {
        const found = this.applyAutoBugButtonTimeReference();
        if (!found) {
          this.delegate.onNoBugButtonFound?.();
        }
      }
    }
  }

  /**
   * Find the last "Bug Report Button Tap" or appEvent "Bug Button pressed" log entry
   * and set it as the base time offset reference row. Returns true if found, false if not.
   */
  private applyAutoBugButtonTimeReference(): boolean {
    let lastBugReportRowIndex: number | undefined;

    for (let i = this._allRowModels.length - 1; i >= 0; i--) {
      const model = this._allRowModels[i];
      if (model.data.type === LogViewerTableRowType.LogEntry) {
        const logEntryRow = model as LogEntryRowModel;
        const content = logEntryRow.logEntry.content;
        if (
          content.includes('Bug Report Button Tap') ||
          (content.includes('"tag":"appEvent"') && content.includes('"Bug Button pressed"'))
        ) {
          lastBugReportRowIndex = i;
          break;
        }
      }
    }

    if (typeof lastBugReportRowIndex !== 'undefined') {
      this.baseTimeOffsetRowIndex = lastBugReportRowIndex;
      return true;
    }
    return false;
  }

  private saveSettingsAndApplyToRows(s: AppSettings) {
    this._modelData.appSettings = _.cloneDeep(s);

    this._allRowModels.forEach((model) => {
      switch (model.data.type) {
        case LogViewerTableRowType.Summary:
        case LogViewerTableRowType.FileDivider:
          break;
        case LogViewerTableRowType.LogEntry:
          const logEntryRow = model as LogEntryRowModel;
          logEntryRow.applySettings(s);
          break;
      }
    });
  }

  set postProcessing(pp: LogViewerPostProcessing) {
    if (!_.isEqual(pp, this._modelData.postProcessing)) {
      this._modelData.postProcessing = _.cloneDeep(pp);

      this.workerClient.syncTableChanges({
        postProcess: true,
        change: {
          postProcessing: this._modelData.postProcessing,
        },
      });
    }
  }

  get selectedRowIndexBeforeFiltering(): number | undefined {
    return this._modelData.selectedRowIndexBeforeFiltering;
  }

  set selectedRowIndexBeforeFiltering(v: number | undefined) {
    const changesToSync: SyncLogEntryRowChangesArgs = { changes: [], postProcess: false };

    const prevSelectedIndex = this._modelData.selectedRowIndexBeforeFiltering;
    if (typeof prevSelectedIndex !== 'undefined') {
      this._allRowModels[prevSelectedIndex].data.isSelected = false;

      changesToSync.changes.push({
        rowIndex: prevSelectedIndex,
        change: {
          isSelected: false,
        },
      });

      const wasLineVisibleBecauseItPassedFilters =
        this._allRowModels[prevSelectedIndex].data.passesFilter;
      if (!wasLineVisibleBecauseItPassedFilters) {
        // It needs to be hidden now because it does not pass filters.
        // But we postpone postprocess because it is not critical to hide it straightaway.
        changesToSync.postProcess = false;
      }
    }

    this._modelData.selectedRowIndexBeforeFiltering = v;
    if (typeof v !== 'undefined') {
      this._allRowModels[v].data.isSelected = true;

      changesToSync.changes.push({
        rowIndex: v,
        change: {
          isSelected: true,
        },
      });

      const wasLineHiddenBefore =
        typeof this._allRowModels[v].data.indexAfterApplyingFilters === 'undefined';
      if (wasLineHiddenBefore) {
        // It needs to be shown now because it is selected.
        changesToSync.postProcess = true;
      }
    }

    this.notifyAboutChange({ shouldClearRowHeightCache: false });
    this.workerClient.syncLogEntryRowChanges(changesToSync);
  }

  getRowsCountToDisplay(): number {
    return this.rowIndicesToShow.length;
  }

  getRowModelToDisplayAt(index: number): LogViewerTableRowModel {
    const rowIndex = this.rowIndicesToShow[index];
    return this._allRowModels[rowIndex];
  }

  createColumn(def: LogViewerTableColumnDefinition) {
    this._additionalColumnDefinitions.push(def);

    def.onChange = () => {
      const i = this._additionalColumnDefinitions.findIndex((c) => c.uniqueId === def.uniqueId);
      this.updateCellsForColumn(i);
    };

    def.onDeleteRequest = () => {
      const i = this._additionalColumnDefinitions.findIndex((c) => c.uniqueId === def.uniqueId);
      this.deleteColumnAtIndex(i);
    };

    this._allRowModels.forEach((rowModel) => {
      switch (rowModel.data.type) {
        case LogViewerTableRowType.LogEntry:
          const entryModel = rowModel as LogEntryRowModel;

          let cellModel: LogEntryCellModel;
          switch (def.type) {
            case LogViewerTableColumnType.NetworkBodyWithJq:
              const colDef = def as LogViewerTableNetworkBodyWithJqColumnDefinition;
              const jqCellModel = NetworkBodyWithJqCellModelFactory.make(
                entryModel.logEntry,
                colDef.jqQuery,
              );
              cellModel = jqCellModel;

              jqCellModel.onRequestCreateNewColumn = (jqQuery) => {
                this.createNewJqQueryBodyColumn(jqQuery);
              };

              break;
          }

          const cells = _.clone(entryModel.additionalCells);
          cells.push(cellModel);
          entryModel.additionalCells = cells;
          break;
        default:
          break; // Do nothing.
      }
    });

    this.notifyAboutChange({ shouldClearRowHeightCache: true });
    this.resetWorkerClient();
  }

  updateCellsForColumn(index: number) {
    const def = this._additionalColumnDefinitions[index];

    this._allRowModels.forEach((rowModel) => {
      switch (rowModel.data.type) {
        case LogViewerTableRowType.LogEntry:
          const entryModel = rowModel as LogEntryRowModel;

          switch (def.type) {
            case LogViewerTableColumnType.NetworkBodyWithJq:
              const colDef = def as LogViewerTableNetworkBodyWithJqColumnDefinition;
              const cellModel = entryModel.additionalCells[index] as NetworkBodyWithJqCellModel;
              cellModel.updateJqQuery(colDef.jqQuery);
              break;
          }

          break;
        default:
          break; // Do nothing.
      }
    });

    this.notifyAboutChange({ shouldClearRowHeightCache: true });
    this.resetWorkerClient();
  }

  deleteColumnAtIndex(index: number) {
    this._additionalColumnDefinitions.splice(index, 1);

    this._allRowModels.forEach((rowModel) => {
      switch (rowModel.data.type) {
        case LogViewerTableRowType.LogEntry:
          const entryModel = rowModel as LogEntryRowModel;
          const cells = _.clone(entryModel.additionalCells);
          cells.splice(index, 1);
          entryModel.additionalCells = cells;
          break;
        default:
          break; // Do nothing.
      }
    });

    this.notifyAboutChange({ shouldClearRowHeightCache: true });
    this.resetWorkerClient();
  }

  deleteAllColumns() {
    this._additionalColumnDefinitions = [];

    this._allRowModels.forEach((rowModel) => {
      switch (rowModel.data.type) {
        case LogViewerTableRowType.LogEntry:
          const entryModel = rowModel as LogEntryRowModel;
          entryModel.additionalCells = [];
          break;
        default:
          break; // Do nothing.
      }
    });

    this.notifyAboutChange({ shouldClearRowHeightCache: true });
    this.resetWorkerClient();
  }

  private constructInitialState() {
    const allModels = this.createAllRowModels();

    this._allRowModels = allModels;
    this._additionalColumnDefinitions = [];

    this._modelData.bookmarkedRowIndicesBeforeFiltering = new Set();

    const storedBookmarkIndicesStr = localStorage.getItem(`bookmarked_${this._log.uniqueId}`);
    if (storedBookmarkIndicesStr) {
      const storedBookmarkIndices: number[] = JSON.parse(storedBookmarkIndicesStr);
      if (Array.isArray(storedBookmarkIndices)) {
        storedBookmarkIndices.forEach((index) => {
          this._modelData.bookmarkedRowIndicesBeforeFiltering.add(index);
          this._allRowModels[index].data.isBookmarked = true;
        });
      }

      this.notifyAboutChangedBookmarkedRows();
    }

    this.rowIndicesToShow = this._allRowModels.map((_, i) => i);
    this.foundOccurancesByHighlighter = [];

    this.resetWorkerClient();
  }

  private resetWorkerClient() {
    const completeData: LogViewerTableModelDataWithRows = {
      tableData: this._modelData,
      // eslint-disable-next-line array-callback-return -- TODO: use forEach
      allRows: this._allRowModels.map((rowModel) => {
        switch (rowModel.data.type) {
          case LogViewerTableRowType.Summary:
          case LogViewerTableRowType.FileDivider:
            return {
              rowData: rowModel.data,
              allCells: [],
            };
          case LogViewerTableRowType.LogEntry:
            const logEntryRowModel = rowModel as LogEntryRowModel;
            return {
              rowData: rowModel.data,
              allCells: [logEntryRowModel.mainCell]
                .concat(logEntryRowModel.additionalCells)
                .map((cell) => cell.data),
            };
        }
      }),
    };

    this.workerClient.reset(completeData);
  }

  private createAllRowModels(): LogViewerTableRowModel[] {
    const rowModels: LogViewerTableRowModel[] = [];
    this.iosRouterTreeSnapshotsByLogEntry = buildIosRouterTreeSnapshotsByLogEntry(
      this._log.entries,
    );

    rowModels.push(LogViewerTableSummaryRowModelFactory.makeFromLog(this._log));

    // Build a quick lookup: entry index → file boundary info, for O(1) lookup
    const fileBoundaryByEntryIndex = new Map<
      number,
      { fileName: string; firstEntryIndex: number }
    >();
    for (const boundary of this._log.fileBoundaries ?? []) {
      fileBoundaryByEntryIndex.set(boundary.firstEntryIndex, boundary);
    }

    let firstLogEntryRowModel: LogEntryRowModel | undefined;
    this._log.entries.forEach((entry) => {
      // Insert a file divider row before the first entry of every subsequent file
      const boundary = fileBoundaryByEntryIndex.get(entry.index);
      if (boundary) {
        const dividerFileName = boundary.fileName.split('/').pop() ?? boundary.fileName;
        rowModels.push(FileDividerRowModelFactory.make(dividerFileName, rowModels.length));
      }

      const mainCellModel = this.createMainLogEntryCellModel(entry);

      const model = LogEntryRowModelFactory.make(
        entry,
        this._modelData.appSettings,
        mainCellModel,
        this,
      );
      rowModels.push(model);

      if (firstLogEntryRowModel) {
        model.data.header.isBaseTimeOffsetFromThisRow = false;
        model.setBaseTimeOffset(
          model.logEntry.secondsSinceFirstLogEntry -
            firstLogEntryRowModel.logEntry.secondsSinceFirstLogEntry,
        );
      } else {
        model.data.header.isBaseTimeOffsetFromThisRow = true;
        model.setBaseTimeOffset(0.0);
        firstLogEntryRowModel = model;
      }
    });

    this.logEntryIndexToRowIndexBeforeFiltering.clear();

    rowModels.forEach((model, index) => {
      model.data.indexBeforeApplyingFilters = index;
      model.data.indexAfterApplyingFilters = index;

      model.delegate = this;

      if (model.data.type === LogViewerTableRowType.LogEntry) {
        const logEntry = model as LogEntryRowModel;
        this.logEntryIndexToRowIndexBeforeFiltering.set(
          logEntry.logEntry.index,
          model.data.indexBeforeApplyingFilters,
        );
      }
    });

    return rowModels;
  }

  private applyUpdatedRowModelAtNonFilteredIndex(updatedRowModel: LogViewerTableRowModel) {
    console.log(
      'applyUpdatedRowModelAtNonFilteredIndex',
      updatedRowModel.data.indexAfterApplyingFilters,
    );
    this._allRowModels[updatedRowModel.data.indexBeforeApplyingFilters] = updatedRowModel;

    if (typeof updatedRowModel.data.indexAfterApplyingFilters !== 'undefined') {
      this.notifyAboutChange({
        shouldClearRowHeightCache: false,
        rowIndicesBeforeFilteringToRefresh: [updatedRowModel.data.indexBeforeApplyingFilters],
      });
    }
  }

  private createMainLogEntryCellModel(logEntry: LogEntry): LogEntryCellModel {
    let result: LogEntryCellModel;

    if (
      logEntry.entryType === LogEntryType.NetworkRequest ||
      logEntry.entryType === LogEntryType.NetworkResponse
    ) {
      const networkCellModel = LogEntryNetworkCellModelFactory.make(logEntry);
      result = networkCellModel;

      networkCellModel.onRequestCreateNewColumn = (jqQuery) => {
        this.createNewJqQueryBodyColumn(jqQuery);
      };
      networkCellModel.onRequestJumpToLogEntry = (logEntryIndex) => {
        this.delegate.onRequestJumpToLogEntry(logEntryIndex);
      };

      networkCellModel.onRequestSurroundingEntries = (entryIndex, direction, count) => {
        const entries = this._log.entries;
        if (direction === 'before') {
          const start = Math.max(0, entryIndex - count);
          return entries.slice(start, entryIndex);
        }
        const end = Math.min(entries.length, entryIndex + count + 1);
        return entries.slice(entryIndex + 1, end);
      };
    } else if (logEntry.entryType === LogEntryType.ReduxAction) {
      result = LogEntryReduxActionCellModelFactory.make(logEntry);
    } else if (logEntry.entryType === LogEntryType.MQTTMessage) {
      const mqttCellModel = LogEntryMQTTCellModelFactory.make(logEntry);
      result = mqttCellModel;

      mqttCellModel.onLogEntryCellModelChange = () => {
        this.delegate.onModelChange({
          shouldClearRowHeightCache: true,
          rowIndicesBeforeFilteringToRefresh: [logEntry.index],
        });
      };
      mqttCellModel.onRequestCreateNewColumn = (jqQuery) => {
        this.createNewJqQueryBodyColumn(jqQuery);
      };
    } else {
      result = LogEntryGenericCellModelFactory.make(
        logEntry,
        this.iosRouterTreeSnapshotsByLogEntry.get(logEntry.index),
      );
    }

    return result;
  }

  private createNewJqQueryBodyColumn(jqQuery: string) {
    const def = new LogViewerTableNetworkBodyWithJqColumnDefinition(jqQuery);
    this.createColumn(def);
  }

  private notifyAboutChangedBookmarkedRows() {
    let bookmarkedRowModels: LogViewerTableRowModel[] = [];

    const indices = Array.from(this.modelData.bookmarkedRowIndicesBeforeFiltering).sort(
      (a, b) => a - b,
    );
    bookmarkedRowModels = indices.map((index) => this.allRowModels[index]);

    localStorage.setItem(`bookmarked_${this._log.uniqueId}`, JSON.stringify(indices));

    this.delegate.onBookmarkedRowsChange(bookmarkedRowModels);
  }

  // MARK: - LogViewerTableRowModelDelegate

  onRequestRedraw(rowModel: LogViewerTableRowModel): void {
    this.applyUpdatedRowModelAtNonFilteredIndex(rowModel);
  }

  onRequestScrollToRow(rowModel: LogViewerTableRowModel): void {
    if (rowModel.data.indexAfterApplyingFilters) {
      this.delegate.onRequestScrollToRowWithIndexAfterFiltering(
        rowModel.data.indexAfterApplyingFilters,
      );
    } else {
      console.log('Cannot scroll to row that is not shown');
    }
  }

  onRequestAddToBookmarks(rowModel: LogViewerTableRowModel): void {
    this._modelData.bookmarkedRowIndicesBeforeFiltering.add(
      rowModel.data.indexBeforeApplyingFilters,
    );
    rowModel.data.isBookmarked = true;

    this.workerClient.syncLogEntryRowChanges({
      changes: [
        {
          rowIndex: rowModel.data.indexBeforeApplyingFilters,
          change: { isBookmarked: true },
        },
      ],
      postProcess: false,
    });

    if (typeof rowModel.data.indexAfterApplyingFilters !== 'undefined') {
      this.notifyAboutChange({
        shouldClearRowHeightCache: false,
        rowIndicesBeforeFilteringToRefresh: [rowModel.data.indexBeforeApplyingFilters],
      });
    }

    this.notifyAboutChangedBookmarkedRows();
  }

  onRequestDeleteFromBookmarks(rowModel: LogViewerTableRowModel): void {
    this._modelData.bookmarkedRowIndicesBeforeFiltering.delete(
      rowModel.data.indexBeforeApplyingFilters,
    );
    rowModel.data.isBookmarked = false;

    this.workerClient.syncLogEntryRowChanges({
      changes: [
        {
          rowIndex: rowModel.data.indexBeforeApplyingFilters,
          change: { isBookmarked: false },
        },
      ],
      postProcess: false,
    });

    if (typeof rowModel.data.indexAfterApplyingFilters !== 'undefined') {
      this.notifyAboutChange({
        shouldClearRowHeightCache: false,
        rowIndicesBeforeFilteringToRefresh: [rowModel.data.indexBeforeApplyingFilters],
      });
    }

    this.notifyAboutChangedBookmarkedRows();
  }

  // MARK: - LogViewerTableLogEntryRowModelDelegate

  onSelectRowAsBaseForTimeOffset(rowModel: LogEntryRowModel): void {
    this.baseTimeOffsetRowIndex = rowModel.data.indexBeforeApplyingFilters;
  }

  onRowDataChange(args: {
    rowModel: LogEntryRowModel;
    change: DeepPartial<LogEntryRowModelData>;
    postProcess: boolean;
  }): void {
    console.log('onRowDataChange', args);

    modelDataDiffMerge(args.rowModel.data, args.change);

    this.notifyAboutChange({
      shouldClearRowHeightCache: false,
      rowIndicesBeforeFilteringToRefresh: [args.rowModel.data.indexBeforeApplyingFilters],
    });

    this.workerClient.syncLogEntryRowChanges({
      changes: [
        {
          rowIndex: args.rowModel.data.indexBeforeApplyingFilters,
          change: args.change,
        },
      ],
      postProcess: args.postProcess,
    });
  }

  onCellDataChange(args: {
    rowModel: LogEntryRowModel;
    cellModel: LogEntryCellModel;
    change: DeepPartial<LogEntryCellModelData>;
    postProcess: boolean;
  }): void {
    console.log('onCellDataChange', args.change);

    modelDataDiffMerge(args.cellModel.data, args.change);

    const changeJson = JSON.stringify(args.change);
    const affectsRowHeight =
      changeJson.includes('expandingState') ||
      changeJson.includes('body') ||
      changeJson.includes('headers') ||
      changeJson.includes('query') ||
      changeJson.includes('contextualEvents');

    this.notifyAboutChange({
      shouldClearRowHeightCache: affectsRowHeight,
      rowIndicesBeforeFilteringToRefresh: affectsRowHeight
        ? undefined
        : [args.rowModel.data.indexBeforeApplyingFilters],
    });

    let cellIndex: number;
    if (args.rowModel.mainCell === args.cellModel) {
      cellIndex = 0;
    } else {
      cellIndex = args.rowModel.additionalCells.indexOf(args.cellModel);
    }

    this.workerClient.syncLogEntryCellChange({
      rowIndex: args.rowModel.data.indexBeforeApplyingFilters,
      cellIndex,
      change: args.change,
      postProcess: args.postProcess,
    });
  }

  // MARK: - LogViewerTableModelWorkerClientDelegate

  onPostProcessStarted(): void {
    this.delegate.postProcessingOperationStarted();
  }

  onPostProcessSucceeded(result: PostProcessingResult): void {
    this.delegate.postProcessingOperationIsAboutToComplete();

    console.time('onPostProcessSucceeded');

    console.timeLog('onPostProcessSucceeded', 'Diff', result);

    this._allRowModels.forEach((rowModel) => {
      // Reset the index because it will be set in rowDiff if row is visible
      // after post processing.
      rowModel.data.indexAfterApplyingFilters = undefined;

      const rowDiff = result.rowChanges[rowModel.data.indexBeforeApplyingFilters].rowData;
      if (typeof rowDiff !== 'undefined') {
        modelDataDiffMerge(rowModel.data, rowDiff);
      }

      const cellsDiff = result.rowChanges[rowModel.data.indexBeforeApplyingFilters].allCells;
      if (typeof cellsDiff !== 'undefined') {
        switch (rowModel.data.type) {
          case LogViewerTableRowType.LogEntry:
            const logEntryRowModel = rowModel as LogEntryRowModel;
            const allCells = [logEntryRowModel.mainCell].concat(logEntryRowModel.additionalCells);
            allCells.forEach((cellModel, cellIndex) => {
              const cellDiff = cellsDiff[cellIndex];
              if (typeof cellDiff !== 'undefined') {
                modelDataDiffMerge(cellModel.data, cellDiff);
              }
            });
            break;
          case LogViewerTableRowType.Summary:
          case LogViewerTableRowType.FileDivider:
            // No cells in this row type.
            break;
        }
      }
    });

    console.timeLog('onPostProcessSucceeded', 'Diff applied', this._allRowModels);

    this.rowIndicesToShow = result.rowIndicesToShow;
    this.foundOccurancesByHighlighter = result.occurancesPerHighlighter;
    this.notifyAboutChange({ shouldClearRowHeightCache: true });

    console.timeEnd('onPostProcessSucceeded');

    this.delegate.postProcessingOperationCompleted(result.occurancesPerHighlighter);
  }

  onPostProcessFailed(error: any): void {
    this.delegate.postProcessingOperationFailed(error);
  }
}
